// Informes por puesto (lote D): convierte las filas que YA guardan los módulos de evaluación en filas del informe, con
// la metodología, el resultado y el valor de referencia que cada módulo ya usa. No agrega normas ni cambia valores:
// los límites salen de las mismas constantes de cada módulo (ruido, iluminación, estrés térmico, NIOSH, BIOGAVAL,
// COSHH, ROSA, Snook y Ciriello) y los niveles de REBA, RULA y la Matriz de Riesgos de su propio resultado.
// "Cumple" en REBA (nivel 0 o 1) y RULA (nivel 1) es la lectura del nivel de actuación del propio método: lo revisa
// Kevin al validar.

import { calcularPorcentajeMH, calcularRiesgoBiologico, LEB, NAB, type BiologicalAgent, type HygieneAnswers } from '../biogaval';
import { diaEc } from '../fechas';
import { EM_TABLE_ART10 } from '../iluminacion';
import { LIMITE_AM196_8H, LIMITE_PICO_DIR_2003_10 } from '../ruido';
import type { Agente, Evaluacion, Modulo } from './ip-tipos';

type J = Record<string, unknown>;
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : null);
const txt = (v: unknown): string => (typeof v === 'string' ? v : v === null || v === undefined ? '' : String(v));
const obj = (v: unknown): J => (v && typeof v === 'object' && !Array.isArray(v) ? (v as J) : {});
const dec = (n: number | null, d = 1) => (n === null ? '—' : n.toFixed(d).replace('.', ','));
// created_at es un instante: el día de Ecuador; fecha_evaluacion (date) queda igual (lib/fechas).
export const dia = (iso: string | null | undefined) => diaEc(iso);

/** Fila genérica de los módulos con job_position_id, inputs y resultado (REBA, RULA, ROSA, Snook, ruido, iluminación, estrés). */
export interface FilaModulo { id: string; job_position_id: string | null; descripcion?: string | null; nombre_local?: string | null;
  tipo_ruido?: string | null; tipo_tarea_art10?: string | null; tipo_tarea?: string | null; lado?: string | null; ambiente?: string | null;
  resultado: unknown; resultado_compuesto?: unknown; cumple_normativa?: boolean | null; created_at: string }

const base = (f: FilaModulo, modulo: Modulo, agente: Agente, metodologia: string) => ({
  id: f.id, modulo, agente, fecha: dia(f.created_at), cambio: f.created_at, titulo: txt(f.descripcion ?? f.nombre_local).trim() || '—', metodologia,
});

export function evaluacionReba(f: FilaModulo): Evaluacion {
  const r = obj(f.resultado); const n = obj(r.nivelActuacion);
  const nivel = num(n.nivel);
  return { ...base(f, 'reba', 'ergonomico', 'REBA (Hignett y McAtamney, 2000)'),
    resultado: `Puntuación ${txt(r.puntuacionFinal) || '—'}: riesgo ${txt(n.riesgo) || '—'}. ${txt(n.actuacion)}`.trim(),
    referencia: 'Niveles de actuación del método REBA (0 a 4)', cumple: nivel === null ? null : nivel <= 1 };
}
export function evaluacionRula(f: FilaModulo): Evaluacion {
  const r = obj(f.resultado); const n = obj(r.nivelActuacion);
  const nivel = num(n.nivel);
  return { ...base(f, 'rula', 'ergonomico', 'RULA (McAtamney y Corlett, 1993)'),
    resultado: `Puntuación ${txt(r.puntuacionFinal) || '—'}: nivel ${nivel ?? '—'}. ${txt(n.descripcion)}`.trim(),
    referencia: 'Niveles de actuación del método RULA (1 = riesgo aceptable)', cumple: nivel === null ? null : nivel === 1 };
}
export function evaluacionRosa(f: FilaModulo): Evaluacion {
  const r = obj(f.resultado);
  const sin = r.nivelAccion === 'sin_accion_inmediata';
  return { ...base(f, 'rosa', 'ergonomico', 'ROSA (Sonne, Villalta y Andrews, 2012)'),
    resultado: `Puntuación ${txt(r.puntuacionFinal) || '—'}: ${sin ? 'sin acción inmediata' : 'acción requerida'}`,
    referencia: 'Puntuación menor que 5: sin acción inmediata', cumple: r.nivelAccion ? sin : null };
}
export function evaluacionNiosh(f: FilaModulo): Evaluacion {
  const r = obj(f.resultado_compuesto);
  const ilc = num(r.ilc); const nivel = txt(r.nivelRiesgo);
  return { ...base(f, 'niosh', 'ergonomico', 'Ecuación de NIOSH (Waters, Putz-Anderson y Garg, 1994)'),
    resultado: `Índice de levantamiento compuesto ${dec(ilc, 2)}: riesgo ${nivel || '—'}`,
    referencia: 'Índice de levantamiento: hasta 1 aceptable, menor que 3 moderado, desde 3 alto', cumple: nivel ? nivel === 'aceptable' : null };
}
export function evaluacionSnook(f: FilaModulo): Evaluacion {
  const r = obj(f.resultado);
  const ok = typeof r.aceptable === 'boolean' ? r.aceptable : null;
  const tipo = txt(f.tipo_tarea);
  const detalle = tipo === 'transporte' ? `peso real ${dec(num(r.pesoReal))} kg frente a máximo aceptable ${dec(num(r.maximoAceptable))} kg`
    : `fuerza inicial ${dec(num(r.fuerzaInicialReal))} frente a ${dec(num(r.inicialMaxima))}; sostenida ${dec(num(r.fuerzaSostenidaReal))} frente a ${dec(num(r.sostenidaMaxima))} kg`;
  return { ...base(f, 'snook', 'ergonomico', 'Snook y Ciriello (1991), percentil 90'),
    resultado: `${tipo ? tipo[0].toUpperCase() + tipo.slice(1) : 'Tarea'}: ${detalle}`, referencia: 'Valores máximos aceptables de las tablas de Snook y Ciriello',
    cumple: ok };
}
export function evaluacionRuido(f: FilaModulo): Evaluacion {
  const r = obj(f.resultado);
  if (f.tipo_ruido === 'impulsivo') {
    return { ...base(f, 'ruido', 'fisico', 'Guía Técnica INSST 2022 (ruido impulsivo)'),
      resultado: `Pico atenuado ${dec(num(r.LCpicoAtenuado))} dB(C)`, referencia: `Valor límite de pico: ${LIMITE_PICO_DIR_2003_10} dB(C)`,
      cumple: typeof f.cumple_normativa === 'boolean' ? f.cumple_normativa : null };
  }
  return { ...base(f, 'ruido', 'fisico', 'NTP 638 (INSHT, 2003)'),
    resultado: `Nivel diario equivalente ${dec(num(r.laeqDiario))} dB(A)`,
    referencia: `${LIMITE_AM196_8H} dB(A) en 8 horas (Anexo 3 del AM 196, por remisión del DE 255 Art. 48)`,
    cumple: typeof f.cumple_normativa === 'boolean' ? f.cumple_normativa : null };
}
export function evaluacionIluminacion(f: FilaModulo): Evaluacion {
  const r = obj(f.resultado); const em = obj(r.emRequerido);
  const tabla = (EM_TABLE_ART10 as Record<string, { label: string; min: number }>)[txt(f.tipo_tarea_art10)];
  const min = num(em.min) ?? tabla?.min ?? null;
  return { ...base(f, 'iluminacion', 'fisico', 'Método de los lúmenes (Art. 10 del Anexo 3 AM 196)'),
    resultado: `Iluminancia ${dec(num(r.iluminanciaResultante), 0)} lux`,
    referencia: `Mínimo ${min ?? '—'} lux para ${txt(em.label) || tabla?.label || 'la tarea'} (Art. 10.4 del Anexo 3 AM 196)`,
    cumple: typeof f.cumple_normativa === 'boolean' ? f.cumple_normativa : null };
}
export function evaluacionEstres(f: FilaModulo): Evaluacion {
  const r = obj(f.resultado);
  return { ...base(f, 'estres_termico', 'fisico', 'Índice WBGT (NTP 1189 del INSST y UNE-EN ISO 7243:2017)'),
    resultado: `WBGT ${dec(num(r.wbgt))} °C (actividad ${txt(r.categoria) || '—'})`,
    referencia: `Límite WBGT ${dec(num(r.limite))} °C para esa actividad`, cumple: typeof r.cumple === 'boolean' ? r.cumple : null };
}

/** Matriz de Riesgos (GTC 45): riesgo inicial y residual. */
export interface FilaIper { id: string; position_id: string | null; process_name?: string | null; activity?: string | null; task?: string | null;
  hazard_category?: string | null; hazard_type?: string | null; nivel_riesgo: number | null; interpretacion_riesgo: string | null;
  aceptabilidad: string | null; nivel_riesgo_residual?: number | null; aceptabilidad_residual?: string | null; fecha_evaluacion: string | null;
  created_at: string; updated_at?: string | null; num_expuestos?: number | null;
  medida_eliminacion?: string | null; medida_sustitucion?: string | null; medida_control_ingenieria?: string | null;
  medida_control_administrativo?: string | null; medida_epp?: string | null }
export function evaluacionIper(f: FilaIper): Evaluacion {
  const acept = f.aceptabilidad_residual || f.aceptabilidad || '';
  const residual = f.nivel_riesgo_residual !== null && f.nivel_riesgo_residual !== undefined
    ? `; residual ${f.nivel_riesgo_residual} (${f.aceptabilidad_residual ?? '—'})` : '';
  return {
    id: f.id, modulo: 'iper', agente: 'seguridad', fecha: dia(f.fecha_evaluacion || f.created_at), cambio: f.updated_at || f.created_at,
    titulo: [f.task, f.hazard_type].filter(Boolean).join(' · ') || txt(f.activity) || '—', metodologia: 'Identificación de peligros y evaluación de riesgos (GTC 45)',
    resultado: `Nivel de riesgo ${f.nivel_riesgo ?? '—'} (${f.interpretacion_riesgo ?? '—'}, ${f.aceptabilidad ?? '—'})${residual}`,
    referencia: 'Aceptabilidad del riesgo según la GTC 45', cumple: acept ? acept === 'Aceptable' : null,
  };
}

/** Riesgo químico por tarea (COSHH Essentials, NTP 936): nivel 1 a 4 y su acción. */
export interface FilaQuimico { id: string; position_id: string | null; product_name_snapshot: string; nivel_riesgo: number; accion_recomendada: string;
  created_at: string }
export function evaluacionQuimico(f: FilaQuimico): Evaluacion {
  return { id: f.id, modulo: 'quimico_tarea', agente: 'quimico', fecha: dia(f.created_at), cambio: f.created_at, titulo: f.product_name_snapshot || '—',
    metodologia: 'COSHH Essentials (NTP 936), evaluación cualitativa', resultado: `Nivel de riesgo ${f.nivel_riesgo}: ${f.accion_recomendada}`,
    referencia: 'Nivel 1: ventilación general suficiente', cumple: f.nivel_riesgo === 1 };
}

/** BIOGAVAL-NEO: R = G + T + P + F − V − MH, calculado como lo hace el módulo, con su nivel. */
export interface FilaBioExposicion { id: string; job_position_id: string; agent_id: string; via_probabilidad: 'prevalencia' | 'incidencia';
  valor_probabilidad: number; frecuencia_pct: number; vacunacion_pct: number; created_at: string;
  biological_agents: { name: string; grupo: 1 | 2 | 3 | 4; vias_transmision: string[] } | null }
export function evaluacionesBiogaval(exposiciones: FilaBioExposicion[], higiene: HygieneAnswers, cambioHigiene: string | null): Evaluacion[] {
  const pct = calcularPorcentajeMH(higiene);
  return exposiciones.map(e => {
    const agente: BiologicalAgent = { id: e.agent_id, name: e.biological_agents?.name ?? '', grupo: e.biological_agents?.grupo ?? 1,
      vias_transmision: (e.biological_agents?.vias_transmision ?? []) as BiologicalAgent['vias_transmision'] };
    const r = calcularRiesgoBiologico(agente, { agent_id: e.agent_id, via_probabilidad: e.via_probabilidad, valor_probabilidad: Number(e.valor_probabilidad),
      frecuencia_pct: Number(e.frecuencia_pct), vacunacion_pct: Number(e.vacunacion_pct) }, pct);
    const cambio = cambioHigiene && cambioHigiene > e.created_at ? cambioHigiene : e.created_at;
    return { id: e.id, modulo: 'biogaval' as const, agente: 'biologico' as const, fecha: dia(e.created_at), cambio, titulo: agente.name || '—',
      metodologia: 'BIOGAVAL-NEO 2018 (INVASSAT)', resultado: `R = ${r.R}: ${r.nivel}`,
      referencia: `Hasta ${NAB} aceptable, hasta ${LEB} mejorable, más de ${LEB} intolerable`, cumple: r.nivel === 'aceptable' };
  });
}

/** Psicosocial: la evaluación cerrada de la empresa y, si una unidad se llama como el puesto, su distribución global. */
export interface PsicoPuesto { id: string; titulo: string; cerrada: string; unidad: { encuestados: number; bajo: number; medio: number; alto: number } | null }
export function evaluacionPsicosocial(p: PsicoPuesto): Evaluacion {
  const u = p.unidad;
  return { id: p.id, modulo: 'psicosocial', agente: 'psicosocial', fecha: dia(p.cerrada), cambio: p.cerrada, titulo: p.titulo || '—',
    metodologia: 'Cuestionario de evaluación de riesgo psicosocial del Ministerio del Trabajo (2018)',
    resultado: u ? `${u.encuestados} encuestados del puesto: ${u.bajo} en riesgo bajo, ${u.medio} medio y ${u.alto} alto`
      : 'Evaluación de la empresa; sin resultados publicables para este puesto (grupo pequeño o sin unidad con su nombre)',
    referencia: 'Niveles bajo, medio y alto del cuestionario', cumple: u ? u.alto === 0 : null };
}
